import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import Header from "go-app/components/Header";
import handleError from "go-app/services/errors";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LoadingContainer } from "go-core/components/Loading";
import RouteHistory from "go-core/utils/routeHistory";
import { NEW_WAY_TO_ENCODING_FILTER_SIGN } from "go-list/core/components/Filter/services";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { CustomFieldTemplateApi } from "go-segment/services/types";
import { apiOrganization } from "../../../../../../../../../../../services/Api/Organization/apiOrganization";
import {
	CompanyInfoApi,
	InvoiceApi,
	InvoiceDetailsApi,
} from "../../../../../../../../../../../services/Api/Organization/types";
import CompanyInfoNotFull from "../../../../../components/Errors/CompanyInfoNotFull";
import InvoiceDetailsNotExist from "../../../../../components/Errors/InvoiceDetailsNotExist";
import InvoiceForm from "../../../../../components/Form/InvoiceForm";
import { getDisplayInsufficientCompanyInfo, getDisplayInvoiceDetailsNotExist } from "../../../../../utils";

interface MatchParams {
	advance_id: string;
}

const OrganizationInvoicesAdvancesEditPage = (): JSX.Element => {
	const [invoice, setInvoice] = useState<InvoiceApi | undefined>(undefined);
	const [invoiceDetails, setInvoiceDetails] = useState<InvoiceDetailsApi | undefined>(undefined);
	const [customFieldsConfig, setCustomFieldsConfig] = useState<CustomFieldTemplateApi[] | undefined>(undefined);
	const [companyInfo, setCompanyInfo] = useState<CompanyInfoApi | undefined>(undefined);
	const { t } = useTranslation();
	const organization = useSelector(selectOrganization);
	const { addFlash } = useFlash();
	const { advance_id } = useParams<MatchParams>();
	const { handleChangeTabTitle } = useBrowserTabTitle();
	const history = useHistory();
	const displayInsufficientCompanyInfo = getDisplayInsufficientCompanyInfo(invoice, companyInfo);
	const displayInvoiceDetailsNotExist = getDisplayInvoiceDetailsNotExist(invoice, invoiceDetails);
	const displayInvoiceForm = !(displayInsufficientCompanyInfo || displayInvoiceDetailsNotExist);

	useEffect(() => {
		const params: Record<string, any> = {
			include:
				"custom_fields,order_items,order_items.tax,items,items.tax,payments,payments.transaction,orders,summary,summary.taxes,recipient,receiver,previous_advances,ksef",
		};
		const cfParams: Record<string, string> = {
			f: btoa(`${NEW_WAY_TO_ENCODING_FILTER_SIGN}resource_type|e=INVOICE`),
			include: "options,constraints",
		};
		Promise.all([
			apiOrganization.getInvoice(Number(advance_id), params),
			apiOrganization.getCompanyInfo(),
			apiOrganization.getCustomFields(cfParams),
			apiOrganization.getInvoiceDetails(),
		])
			.then(([invoiceResponse, companyInfoResponse, cfResponse, configResponse]) => {
				setInvoice(invoiceResponse);
				setCustomFieldsConfig(cfResponse);
				setInvoiceDetails(configResponse);
				setCompanyInfo(companyInfoResponse);
				handleChangeTabTitle(`${invoiceResponse.number} | ${t("modules.invoice.header.title")}`);
			})
			.catch((err) => {
				handleError.alert(err, addFlash);
			});
	}, []);

	if (!invoice || !customFieldsConfig || !invoiceDetails) {
		return <LoadingContainer />;
	}

	const goBack = () => RouteHistory.goBack(history, `/${organization.id}/invoices/advances/${advance_id}`);

	return (
		<>
			<Header title={`${t("modules.invoice.header.title")} ${invoice.number}`} back={goBack} />
			{displayInvoiceDetailsNotExist && <InvoiceDetailsNotExist />}
			{displayInsufficientCompanyInfo && <CompanyInfoNotFull />}
			{displayInvoiceForm && (
				<InvoiceForm
					invoiceDetails={invoiceDetails}
					invoice={invoice}
					customFieldsConfig={customFieldsConfig}
				/>
			)}
		</>
	);
};
export default OrganizationInvoicesAdvancesEditPage;
